import {StepVariables, type StepData} from '../types/general';

/**
 * Buffered entry produced by an API override during a step-mode run. Each
 * entry carries the api id plus the world-state snapshots taken before and
 * after the call.
 */
export interface ApiBufferEntry {
  apiId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  pre?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  post?: any;
}

/**
 * Minimal lookup-and-iterate surface required by {@link addApiDataToTrace}.
 * `WorkerApi` passes its `CappedMap`, but tests can pass any compatible map.
 */
export interface ApiBufferLike {
  get(key: string): ApiBufferEntry | undefined;
  [Symbol.iterator](): IterableIterator<[string, ApiBufferEntry]>;
}

/**
 * Minimal iterate surface required by {@link addApiDataToTrace}. Only the
 * api ids matter — the cached value is unused.
 */
export interface LibrariesCacheLike {
  [Symbol.iterator](): IterableIterator<[string, unknown]>;
}

/**
 * Walk the trace and attach the per-step API state map under
 * `StepVariables.API_DATA`. For each step that owns an API call (via its
 * `API_CALL_POINTER`), the corresponding api id is advanced to that call's
 * `post` snapshot before the map is attached.
 *
 * Mutates `traceData` in place and returns it for convenience.
 *
 * @param params.traceData The trace to annotate.
 * @param params.apiBuffer Buffer of API call snapshots keyed by call pointer.
 * @param params.librariesCache Registered API libraries (only ids are read).
 * @return The same `traceData` reference, with `API_DATA` populated on each step.
 */
export function addApiDataToTrace({
  traceData,
  apiBuffer,
  librariesCache,
}: {
  traceData: StepData[];
  apiBuffer: ApiBufferLike;
  librariesCache: LibrariesCacheLike;
}): StepData[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  const apiStateMap: Record<string, any> = {};
  for (const [apiId] of librariesCache) {
    // get earliest data in buffer for this api
    for (const [, data] of apiBuffer) {
      if (data.apiId === apiId) {
        apiStateMap[apiId] = data.pre || data.post;
        break;
      }
    }
  }

  traceData.forEach((step) => {
    const apiPtr = step[StepVariables.API_CALL_POINTER];
    if (apiPtr > -1) {
      const data = apiBuffer.get(apiPtr);
      if (data) {
        apiStateMap[data.apiId] = data.post;
      }
    }
    // Snapshot the running map per step. Without the spread, every step
    // would alias the same `apiStateMap` reference and consumers scrubbing
    // the trace would always see the post-run final state.
    step[StepVariables.API_DATA] = {...apiStateMap};
  });

  return traceData;
}
